import { useMemo, useRef, useState } from 'react';

interface Option {
  value: string;
  label: string;
}

interface SearchSelectProps {
  label?: string;
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  allowCustom?: boolean;
}

/**
 * Select con busqueda (usado en Actividad): permite elegir de una lista
 * parametrizada o escribir un valor libre cuando allowCustom = true.
 */
export function SearchSelect({ label, options, value, onChange, placeholder, error, allowCustom }: SearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const selectedLabel = options.find((o) => o.value === value)?.label ?? '';

  return (
    <div className="relative w-full" ref={containerRef}>
      {label && <span className="mb-1.5 block text-sm font-medium text-ink-700">{label}</span>}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={[
          'flex h-[50px] w-full items-center justify-between rounded-xl border bg-surface px-4 text-left text-[15px]',
          'transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-200',
          error ? 'border-critical' : 'border-ink-200',
        ].join(' ')}
      >
        <span className={selectedLabel ? 'text-ink-800' : 'text-ink-400'}>
          {selectedLabel || placeholder || 'Seleccionar...'}
        </span>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-ink-400">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-30 mt-2 w-full overflow-hidden rounded-xl border border-ink-100 bg-surface shadow-float">
          <div className="border-b border-ink-100 p-2">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar actividad..."
              className="h-10 w-full rounded-lg bg-ink-50 px-3 text-sm outline-none"
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                  setQuery('');
                }}
                className="flex w-full items-center px-4 py-3 text-left text-[15px] hover:bg-brand-light"
              >
                {opt.label}
              </button>
            ))}
            {filtered.length === 0 && allowCustom && query && (
              <button
                type="button"
                onClick={() => {
                  onChange(query);
                  setOpen(false);
                  setQuery('');
                }}
                className="flex w-full items-center px-4 py-3 text-left text-[15px] text-brand-600 hover:bg-brand-light"
              >
                Usar "{query}"
              </button>
            )}
            {filtered.length === 0 && !allowCustom && (
              <div className="px-4 py-3 text-sm text-ink-400">Sin resultados</div>
            )}
          </div>
        </div>
      )}
      {error && <span className="mt-1 block text-xs text-critical">{error}</span>}
    </div>
  );
}
